import { useRef } from 'react';
import stickerPreview from '@/assets/hub/sticker_preview.gif';
import { IconHeart } from '@/components/icons';
import { ModeSwitchTrigger } from '@/components/mode/ModeSwitch';
import { ModeWordmark } from '@/components/mode/ModeWordmark';
import { track } from '@/lib/analytics';
import { useLearnerPreferences } from '@/providers/LearnerPreferences';

export type StreakSnapshot = {
  current_streak: number;
  longest_streak: number;
  qualified_today: boolean;
};

type HubHeaderProps = {
  streak?: StreakSnapshot | null;
  onStreakPress?: () => void;
};

export function HubHeader({ streak, onStreakPress }: HubHeaderProps) {
  const { dialect } = useLearnerPreferences();
  const headerRowRef = useRef<HTMLDivElement>(null);
  const streakLabel = (streak?.current_streak ?? 0) > 0 ? `${streak?.current_streak}-day streak` : 'Start a streak';

  return (
    <header className="px-5 sm:px-8">
      <div ref={headerRowRef} className="relative flex items-center justify-between gap-3">
        <div className="relative z-10 flex w-[100px] items-center gap-2">
          <ModeSwitchTrigger source="learning_hub" anchorRef={headerRowRef} />
        </div>
        <p
          data-testid="hub-mode-title"
          className="pointer-events-none absolute inset-x-0 text-center"
        >
          <ModeWordmark dialect={dialect} />
        </p>
        <div className="relative z-10 flex w-[100px] items-center justify-end gap-2">
          <a
            href="/app/stickers"
            aria-label="Sticker packs"
            onClick={() => track('Sticker Packs Press', { source: 'learning_hub' })}
            className="grid h-11 w-11 place-items-center overflow-hidden rounded-full bg-[#399918]"
          >
            <img src={stickerPreview} alt="" className="h-8 w-8 object-contain" />
          </a>
          <a
            href="/app/flashcards/favorites"
            aria-label="Favorite flashcards"
            className="grid h-11 w-11 place-items-center rounded-full bg-[#399918] text-white"
          >
            <IconHeart size={22} filled />
          </a>
        </div>
      </div>
      <button
        type="button"
        onClick={() => {
          track('Streak Chip Press', {
            'Current Streak': streak?.current_streak ?? 0,
            'Qualified Today': streak?.qualified_today ?? false,
          });
          onStreakPress?.();
        }}
        aria-label={streakLabel}
        className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-bold ${
          streak?.qualified_today ? 'bg-[#C96B2B] text-white' : 'bg-[#FFF1E4] text-[#C96B2B]'
        }`}
      >
        <span aria-hidden>🔥</span>
        {streakLabel}
        {streak?.qualified_today ? <span className="text-xs font-extrabold uppercase">Done</span> : null}
      </button>
    </header>
  );
}
